<script setup>
    import { useProjectListStore } from '@/lib/stores/project-list-store';
    import { FilterType, ProjectListMenu } from '@/lib/constants/constants';
    import { computed, defineComponent, onMounted } from 'vue';
    import Filters from '../project-list/Filters.vue';
    import Navigation from '../project-list/Navigation.vue';
    import TagFilters from '../project-list/TagFilters.vue';
    import Map from '../project-list/Map.vue';
    import FirmFilters from '../project-list/FirmFilters.vue';
    import TimePeriodFilters from '../project-list/TimePeriodFilters.vue';
    import Rellax from 'rellax';
    import { MapStyle } from '@/lib/constants/constants';

    const store = useProjectListStore();

    const props = defineProps({
        pages: Array,
        allArticles: Array,
        tagsTitle: String,
        timePeriodTitle: String,
        firmsTitle: String,
        minYear: Number,
        maxYear: Number,
        tagsFilterResult: Array,
        firmsFilterResult: Object,
        mapData: Object
    });

    onMounted(() => {
        store.initPages(props.pages);
        store.initFilters(props.tagsFilterResult, FilterType.TAG);
        store.initSliderValues(props.minYear, props.maxYear);

        for (const [key, value] of Object.entries(props.firmsFilterResult)) {
            store.initFilters(value, FilterType.FIRM);
        }
        let rellax = new Rellax('.rellax');
    });

    const navigate = (article) => {
        window.location.href = article.url;
    };

    const articleContainsAnyTag = (article, tagFilters) => {
        if (tagFilters.length == 0) {
            return true;
        }

        var result = false;
        tagFilters.forEach((f) => {
            var tagExists = article.tags.find((tag) => tag === f.name) ? true : article.category.name === f.name ? true : false;
            if (tagExists) {
                result = true;
            }
        });

        return result;
    };

    const articleContainsAnyFirm = (article, firmFilters) => {
        if (firmFilters.length == 0) {
            return true;
        }

        var result = false;

        firmFilters.forEach((f) => {
            var architectIds = article.architects.map((a) => a.id);
            var architectExist = architectIds.find((id) => id == f.id) ? true : false;

            if (architectExist) {
                result = true;
            }
        });

        return result;
    };

    const articlesResult = computed(() => {
        var filters = store.filters;
        var result = props.allArticles;

        // if no filters, show all articles
        if (
            filters.length === 0 &&
            store.sliderValues[0] === store.sliderInitialValues[0] &&
            store.sliderValues[1] === store.sliderInitialValues[1]
        ) {
            return result;
        }

        var tagFilters = filters.filter((f) => f.filterType === FilterType.TAG);
        var firmFilters = filters.filter((f) => f.filterType === FilterType.FIRM);

        result = result.filter((article) => {
            var showBasedTagFilters = articleContainsAnyTag(article, tagFilters);
            var showBasedOnFirmFilters = articleContainsAnyFirm(article, firmFilters);

            //filter based on slider values (min and max year of publish date)
            let date = new Date(article.publishDate);
            let year = date?.getFullYear();
            var showBasedOnSliderValues =
                year <= store.sliderValues[1] && year >= store.sliderValues[0];

            return showBasedTagFilters && showBasedOnFirmFilters && showBasedOnSliderValues;
        });

        return result;
    });

    const numberOfProjects = articlesResult?.value?.length;

    let rellaxIndex = 0;

    const getRellaxSpeed = (index) => {
        rellaxIndex++;
        if(rellaxIndex > 4)
            rellaxIndex = 1;

        if (rellaxIndex === 1) {
            return -2;
        }
        if (rellaxIndex === 2) {
            return 0;
        }
        if (rellaxIndex === 3) {
            return -3;
        }
        if (rellaxIndex === 4) {
            return -1;
        }
    };

    let rellaxTableIndex = 0;

    const getRellaxSpeedTablet = (index) => {
        rellaxTableIndex++;
        if(rellaxTableIndex > 3)
            rellaxTableIndex = 1;

        if (rellaxTableIndex === 1) {
            return -2;
        }
        if (rellaxTableIndex === 2) {
            return 0;
        }
        if (rellaxTableIndex === 3) {
            return -1;
        }
    };

    const getRellaxSpeedMobile = (index) => {
        if (index % 2 === 2 - 1) {
            return -2;
        }
        else {
            return -1;
        }
    };

    const projectResultMarginBottom = computed(() => {
        return `margin-bottom: ${numberOfProjects * 80}px;`;
    });
</script>

<template>
    <div class="container project-list">
        <div class="row project-list-filter-navigation">
            <Filters class="col-md-6" />
            <Navigation class="col-md-6" :menu="props.pages" />
        </div>

        <div class="row" v-if="store.activePage.id === 2">
            <TagFilters
                :tags-title="props.tagsTitle"
                :tags-filter-result="props.tagsFilterResult"
            />
            <TimePeriodFilters
                :time-period-title="props.timePeriodTitle"
                :min-year="props.minYear"
                :max-year="props.maxYear"
            />
            <FirmFilters
                :firms-title="props.firmsTitle"
                :firms-filter-result="props.firmsFilterResult"
            />
        </div>

        <!-- Desktop -->
        <div
            v-if="store.activePage.id !== 1"
            class="row project-result d-none d-xl-flex"
            :style="projectResultMarginBottom"
        >
            <a
                class="col-6 col-md-4 col-xl-3 project-preview rellax"
                v-on:click="navigate(article)"
                v-on:keyup.enter="navigate(article)"
                v-for="(article, index) in articlesResult"
                :data-rellax-speed="getRellaxSpeed(index)"
                :key="article.id"
                tabindex="0"
            >
                <div>
                    <h2 class="h2-standard underline">{{ article.title }}</h2>
                    <ul class="project-result-list h2-standard">
                        <li v-for="firm in article.architects" :key="firm.id">
                            {{ firm.name }}
                        </li>
                    </ul>
                </div>
                <img :src="article.image" :alt="article.imageAlt" loading="lazy" />
            </a>
        </div>

        <!-- Tablet -->
        <div
            v-if="store.activePage.id !== 1"
            class="project-result d-none d-md-flex d-xl-none row"
            :style="projectResultMarginBottom"
        >
            <a
                class="col-6 col-md-4 col-xl-3 project-preview rellax"
                v-on:click="navigate(article)"
                v-on:keyup.enter="navigate(article)"
                v-for="(article, index) in articlesResult"
                :data-rellax-speed="getRellaxSpeedTablet(index)"
                :key="article.id"
                tabindex="0"
            >
                <div>
                    <h4 class="h2-standard underline">{{ article.title }}</h4>
                    <ul class="project-result-list h2-standard">
                        <li v-for="firm in article.architects" :key="firm.id">
                            {{ firm.name }}
                        </li>
                    </ul>
                </div>
                <img :src="article.image" :alt="article.imageAlt" loading="lazy" />
            </a>
        </div>

        <!-- Mobile -->
        <div
            v-if="store.activePage.id !== 1"
            class="row project-result d-md-none"
            :style="projectResultMarginBottom"
        >
            <a
                class="col-6 col-md-4 col-xl-3 project-preview rellax"
                v-on:click="navigate(article)"
                v-on:keyup.enter="navigate(article)"
                v-for="(article, index) in articlesResult"
                :data-rellax-speed="getRellaxSpeedMobile(index)"
                :key="article.id"
                tabindex="0"
            >
                <div>
                    <h4 class="h2-standard underline">{{ article.title }}</h4>
                    <ul class="project-result-list h2-standard">
                        <li v-for="firm in article.architects" :key="firm.id">
                            {{ firm.name }}
                        </li>
                    </ul>
                </div>
                <img :src="article.image" :alt="article.imageAlt" loading="lazy" />
            </a>
        </div>

        <div v-if="store.activePage.id === 1">
            <Map :map-data="props.mapData" :map-style="MapStyle.LANDING" class="row-full" />
            <!-- <Map :map-data="geoTestJson" /> -->
        </div>
    </div>
</template>
